import React from "react";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { AiOutlineClockCircle } from "react-icons/ai";
import { RiTimeLine } from "react-icons/ri";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";

import theme from "theme/themeThree";
import { CountDownRenderer } from "MVVM/Views/Competitions/Views/Play/countdownTimer";
import { ProForADayButton } from "MVVM/Components/AdRemovalCTA/Components/ProForADayButton";
import { analytics } from "MVVM/Analytics";
import { StripePaystationState } from "MVVM/State";

interface IClipEligibilityModalProps {
  canCreateClipAt?: string;
  onClose: () => void;
  closeQuickEditModal?: () => void;
}

export const ClipEligibilityModal = ({
  canCreateClipAt,
  onClose,
  closeQuickEditModal,
}: IClipEligibilityModalProps) => {
  const [paystation, setPaystationOpen] = useRecoilState(StripePaystationState);
  const history = useHistory();

  const handleUpgradeClick = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "clip_eligibility_upgrade_to_essentials",
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();

    setPaystationOpen({
      ...paystation,
      isModalOpen: true,
      type: "subscribe",
      lookUp: "essentials_monthly",
      returnUrl: window.location.pathname,
    });
  };

  const handleSeeAllPlansClick = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "clip_eligibility_see_all_plans",
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();
    history.push("/upgrade");
  };

  return (
    <ModalContainer>
      <IconContainer>
        <ClockIcon />
      </IconContainer>

      <ContentSection>
        <Title variant="text.headingXSmall" color="ice">
          Clips are Limited on Free Plans
        </Title>

        <ReasonText variant="text.paragraphMedium" color="chalk">
          Sorry, but Allstar is now a limited product on Free accounts. Upgrade
          to Essentials for just $0.99 right now to enjoy unlimited clips, or
          buy Pro for a day with Creator Credits!
        </ReasonText>

        {canCreateClipAt && (
          <TimerSection>
            <Flex alignItems="center" style={{ gap: 6 }}>
              <TimerIcon />
              <Text variant="text.labelMedium" color="chalkAlpha80">
                Next clip available in:
              </Text>
            </Flex>
            <Countdown
              date={Number(canCreateClipAt)}
              renderer={({ days, hours, minutes, seconds }) => {
                if (days > 0) {
                  const timeString = `${hours < 10 ? "0" : ""}${hours}:${
                    minutes < 10 ? "0" : ""
                  }${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

                  return (
                    <Flex alignItems="center" style={{ gap: 8 }}>
                      <Text
                        color="ice"
                        variant="text.headingSmall"
                        fontWeight={600}
                      >
                        {days} {days === 1 ? "day" : "days"}
                      </Text>
                      <Text
                        color="chalkAlpha60"
                        variant="text.labelMedium"
                        fontWeight={500}
                      >
                        {timeString}
                      </Text>
                    </Flex>
                  );
                }

                return (
                  <CountDownRenderer
                    days={days}
                    hours={hours}
                    minutes={minutes}
                    seconds={seconds}
                    color={theme.colors.ice}
                    variant="text.headingSmall"
                    showEndsIn={false}
                    showIcon={false}
                  />
                );
              }}
            />
          </TimerSection>
        )}
      </ContentSection>

      <ButtonContainer>
        <ProForADayButtonWrapper>
          <ProForADayButton
            location="clip_eligibility_modal"
            onSuccess={onClose}
            fullWidth
          />
        </ProForADayButtonWrapper>
        <UpgradeButtonWrapper>
          <UpgradeButton onClick={handleUpgradeClick}>
            <Text variant="text.labelSmall" fontWeight={600} color="ice">
              Upgrade to Essentials
            </Text>
          </UpgradeButton>
          <SeeAllPlansLink onClick={handleSeeAllPlansClick}>
            See all plans
          </SeeAllPlansLink>
        </UpgradeButtonWrapper>
      </ButtonContainer>
    </ModalContainer>
  );
};

const ModalContainer = styled(Flex)`
  align-items: center;
  background-color: ${theme.colors.grey850};
  border: 1px solid ${theme.colors.chalkAlpha20};
  border-radius: 12px;
  flex-direction: column;
  gap: 24px;
  max-width: 500px;
  padding: 32px;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    max-width: 90vw;
    padding: 24px;
  }
`;

const IconContainer = styled(Flex)`
  align-items: center;
  background: ${theme.colors.chalkAlpha10};
  border-radius: 50%;
  height: 64px;
  justify-content: center;
  width: 64px;
`;

const ClockIcon = styled(AiOutlineClockCircle)`
  color: ${theme.colors.ice};
  height: 32px;
  width: 32px;
`;

const TimerIcon = styled(RiTimeLine)`
  color: ${theme.colors.chalkAlpha80};
  height: 16px;
  width: 16px;
`;

const ContentSection = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  width: 100%;
`;

const Title = styled(Text)`
  font-weight: 600;
  line-height: 1.2;
`;

const ReasonText = styled(Text)`
  line-height: 1.5;
  max-width: 400px;
`;

const TimerSection = styled(Flex)`
  align-items: center;
  background: ${theme.colors.chalkAlpha10};
  border-radius: 8px;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding: 20px;
  width: 100%;
`;

const ButtonContainer = styled(Flex)`
  align-items: stretch;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breaks.small}) {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const ProForADayButtonWrapper = styled(Flex)`
  flex: 1;
  justify-content: center;
`;

const UpgradeButtonWrapper = styled(Flex)`
  align-items: center;
  flex: 1;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
`;

const UpgradeButton = styled(Flex)`
  align-items: center;
  background: ${theme.colors.envy};
  border-radius: 8px;
  color: ${theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  justify-content: center;
  padding: 8px 16px;
  text-align: center;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: 100%;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 6px 12px;
  }
`;

const SeeAllPlansLink = styled(Text)`
  color: ${theme.colors.chalkAlpha80};
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.ice};
  }
`;
